import { useState } from 'react';
import { AlertCircle, Search, X, UserPlus } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useChat, ChatContact } from '@/hooks/chat/useChat';
import { ChatRoom } from './ChatRoom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Painel lateral de chat com lista de conversas e busca
 * UX melhorada: botão de nova conversa e contatos visíveis
 */
export const ChatPanel = ({ isOpen, onClose }: ChatPanelProps) => {
  const [selectedContact, setSelectedContact] = useState<ChatContact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  
  const { contacts, conversations, contactsLoading, contactsError, conversationsLoading } = useChat();

  const isLoading = contactsLoading || conversationsLoading;

  // Filtrar conversas pela busca
  const filteredConversations = conversations.filter(c =>
    c.nome_completo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Contatos disponíveis para nova conversa (excluindo os que já têm conversa)
  const availableContacts = contacts.filter(c =>
    !conversations.some(conv => conv.user_id === c.user_id)
  );

  // Filtrar contatos pela busca
  const filteredContacts = availableContacts.filter(c =>
    c.nome_completo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectContact = (contact: ChatContact | { user_id: string; nome_completo: string; setor: string | null }) => {
    setSelectedContact({
      user_id: contact.user_id,
      nome_completo: contact.nome_completo,
      setor: contact.setor,
      unread_count: 0,
    });
    setShowNewChat(false);
    setSearchTerm('');
  };

  const handleBack = () => {
    setSelectedContact(null);
  };

  const handleNewChat = () => {
    setShowNewChat(true);
    setSearchTerm('');
  };

  const handleCancelNewChat = () => {
    setShowNewChat(false);
    setSearchTerm('');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Se não há conversas E há contatos, mostrar contatos diretamente
  const showContactsDirectly = conversations.length === 0 && availableContacts.length > 0;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        {selectedContact ? (
          <ChatRoom 
            contact={selectedContact} 
            onBack={handleBack} 
          />
        ) : (
          <>
            <SheetHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <SheetTitle>Chat</SheetTitle>
                <div className="flex items-center gap-1">
                  {/* Botão de nova conversa */}
                  {!showNewChat && !showContactsDirectly && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={handleNewChat}
                      title="Nova conversa"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}
                  {showNewChat && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleCancelNewChat}
                    >
                      Voltar
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Busca */}
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={showNewChat || showContactsDirectly ? "Buscar contato..." : "Buscar conversa..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </SheetHeader>

            <ScrollArea className="flex-1">
              {/* Estado de erro */}
              {contactsError && (
                <Alert variant="destructive" className="m-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Erro ao carregar contatos. Tente novamente.
                  </AlertDescription>
                </Alert>
              )}

              {/* Estado de loading */}
              {isLoading && !contactsError && (
                <div className="p-4 space-y-3">
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                </div>
              )}

              {/* MODO: Nova conversa ou sem conversas - mostrar contatos */}
              {!isLoading && (showNewChat || showContactsDirectly) && (
                <div className="p-2">
                  <p className="text-xs font-medium text-muted-foreground px-2 py-2 uppercase tracking-wide">
                    {showContactsDirectly ? 'Selecione um contato para iniciar' : 'Iniciar nova conversa'}
                  </p>
                  
                  {filteredContacts.length > 0 ? (
                    filteredContacts.map((contact) => (
                      <button
                        key={contact.user_id}
                        onClick={() => handleSelectContact(contact)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-left"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {getInitials(contact.nome_completo)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <span className="font-medium truncate block">{contact.nome_completo}</span>
                          {contact.setor && (
                            <p className="text-xs text-muted-foreground">{contact.setor}</p>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      {searchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato disponível'}
                    </div>
                  )}
                </div>
              )}

              {/* MODO: Lista de conversas existentes */}
              {!isLoading && !showNewChat && !showContactsDirectly && (
                <>
                  {filteredConversations.length > 0 ? (
                    <div className="p-2">
                      <p className="text-xs font-medium text-muted-foreground px-2 py-2 uppercase tracking-wide">
                        Conversas recentes
                      </p>
                      {filteredConversations.map((conv) => (
                        <button
                          key={conv.user_id}
                          onClick={() => handleSelectContact(conv)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-left"
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(conv.nome_completo)}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium truncate">{conv.nome_completo}</span>
                              {conv.last_message && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(conv.last_message.created_at), {
                                    addSuffix: false,
                                    locale: ptBR,
                                  })}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-muted-foreground truncate">
                                {conv.last_message?.attachment_name 
                                  ? `📎 ${conv.last_message.attachment_name}`
                                  : conv.last_message?.content || 'Sem mensagens'}
                              </p>
                              {conv.unread_count > 0 && (
                                <Badge variant="default" className="ml-2 h-5 min-w-5 flex items-center justify-center p-0 text-xs">
                                  {conv.unread_count}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <p>Nenhuma conversa encontrada</p>
                      <p className="text-sm mt-1">
                        Clique em <UserPlus className="h-3 w-3 inline mx-1" /> para iniciar uma nova
                      </p>
                    </div>
                  )}

                  {/* Seção de contatos disponíveis (abaixo das conversas) */}
                  {availableContacts.length > 0 && filteredConversations.length > 0 && (
                    <>
                      <Separator className="my-2" />
                      <div className="p-2">
                        <p className="text-xs font-medium text-muted-foreground px-2 py-2 uppercase tracking-wide">
                          Outros contatos
                        </p>
                        {filteredContacts.slice(0, 5).map((contact) => (
                          <button
                            key={contact.user_id}
                            onClick={() => handleSelectContact(contact)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-left"
                          >
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-secondary text-secondary-foreground">
                                {getInitials(contact.nome_completo)}
                              </AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 min-w-0">
                              <span className="font-medium truncate block">{contact.nome_completo}</span>
                              {contact.setor && (
                                <p className="text-xs text-muted-foreground">{contact.setor}</p>
                              )}
                            </div>
                          </button>
                        ))}
                        {filteredContacts.length > 5 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-1 text-muted-foreground"
                            onClick={handleNewChat}
                          >
                            Ver todos os {filteredContacts.length} contatos
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};